import { useEffect } from 'react';
import type {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  FieldError,
  FieldLabel,
  SectionCard,
  CampSelect,
  campInputClassFor,
} from '@/features/campaigns/components/FormPrimitives';
import { inputValidation } from '@/utils/function';
import {
  BUSINESS_CATEGORIES,
  getBusinessSubCategories,
  type AddClientFormValues,
} from '../AddClientPage.types';

const CLIENT_NAME_MIN = 3;
const CLIENT_NAME_MAX = 50;

type BusinessDetailsSectionProps = {
  register: UseFormRegister<AddClientFormValues>;
  errors: FieldErrors<AddClientFormValues>;
  watch: UseFormWatch<AddClientFormValues>;
  setValue: UseFormSetValue<AddClientFormValues>;
  variant?: 'card' | 'plain';
};

export function BusinessDetailsSection({
  register,
  errors,
  watch,
  setValue,
  variant = 'card',
}: BusinessDetailsSectionProps) {
  const selectedCategory = watch('businessCategory');
  const subCategories = getBusinessSubCategories(selectedCategory);
  const selectedSubCategory = watch('businessSubCategory');
  const clientName = watch('clientName') || '';
  const inputClass = campInputClassFor(variant);

  useEffect(() => {
    if (!selectedCategory) {
      if (selectedSubCategory) {
        setValue('businessSubCategory', '', { shouldValidate: true });
      }
      return;
    }
    if (selectedSubCategory && !subCategories.includes(selectedSubCategory)) {
      setValue('businessSubCategory', '', { shouldValidate: true });
    }
  }, [selectedCategory, selectedSubCategory, subCategories, setValue]);

  return (
    <SectionCard
      step={1}
      title="Business details"
      description="Tell us about your business ?"
      variant={variant}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <FieldLabel required>Client Name / Company Name</FieldLabel>
          <input
            type="text"
            placeholder="Company or brand name"
            className={inputClass}
            minLength={CLIENT_NAME_MIN}
            maxLength={CLIENT_NAME_MAX}
            aria-invalid={Boolean(errors.clientName)}
            {...register('clientName', {
              required: 'Client name is required',
              minLength: {
                value: CLIENT_NAME_MIN,
                message: `Client name must be at least ${CLIENT_NAME_MIN} characters`,
              },
              maxLength: {
                value: CLIENT_NAME_MAX,
                message: `Client name cannot exceed ${CLIENT_NAME_MAX} characters`,
              },
              setValueAs: (value) =>
                inputValidation(String(value ?? '')).slice(0, CLIENT_NAME_MAX),
              onChange: (e) => {
                e.target.value = inputValidation(e.target.value).slice(
                  0,
                  CLIENT_NAME_MAX,
                );
              },
            })}
          />
          <div className="mt-1.5 flex items-start justify-between gap-3">
            <FieldError message={errors.clientName?.message} />
            <span
              className={`ml-auto shrink-0 text-[11px] tabular-nums ${
                clientName.length >= CLIENT_NAME_MAX
                  ? 'font-semibold text-[var(--dash-brand)]'
                  : 'text-[var(--dash-muted)]'
              }`}
            >
              {clientName.length}/{CLIENT_NAME_MAX}
            </span>
          </div>
        </div>
        <div>
          <FieldLabel required>Category of Business</FieldLabel>
          <CampSelect
            variant={variant}
            aria-invalid={Boolean(errors.businessCategory)}
            {...register('businessCategory', { required: 'Select category' })}
          >
            <option value="">Select category</option>
            {BUSINESS_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </CampSelect>
          <FieldError message={errors.businessCategory?.message} />
        </div>
        <div>
          <FieldLabel required>Sub Category of Business</FieldLabel>
          <CampSelect
            variant={variant}
            disabled={!selectedCategory}
            aria-invalid={Boolean(errors.businessSubCategory)}
            {...register('businessSubCategory', {
              required: 'Select sub category',
            })}
          >
            <option value="">
              {selectedCategory ? 'Select sub category' : 'Select category first'}
            </option>
            {subCategories.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </CampSelect>
          <FieldError message={errors.businessSubCategory?.message} />
        </div>
      </div>
    </SectionCard>
  );
}
